import React from 'react'
import { Link } from 'react-router-dom'
import { Spinner, SpinnerSize } from '@fluentui/react'
import {
  DailyProductionReading,
  SolarMonitoringConnectionStatus,
  SolarMonitoringEvidence,
} from '@oneethos/shared'
import { Section } from '../components'
import { useApi } from '../hooks'

type MonitoringSiteSummary = {
  id: string
  provider: string
  externalSystemId: string
  name?: string
  address?: string
  providerStatus?: string
  systemSizeWatts?: number
  status: SolarMonitoringConnectionStatus
  productionReadings: DailyProductionReading[]
  evidence?: SolarMonitoringEvidence
  lastSyncedAt?: string
  lastSyncErrorCategory?: string
}

type SiteRowProps = {
  site: MonitoringSiteSummary
}

const formatTimestamp = (value?: string): string => {
  if (!value) {
    return '—'
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return '—'
  }
  return parsed.toLocaleString()
}

const formatProduction = (readings: DailyProductionReading[]): string => {
  if (readings.length === 0) {
    return '—'
  }
  return readings
    .slice(-3)
    .map(reading => `${reading.date}: ${(reading.productionWh / 1000).toFixed(2)} kWh`)
    .join(', ')
}

const SiteRow = ({ site }: SiteRowProps) => (
  <tr>
    <td className="text-capitalize">{site.provider}</td>
    <td>{site.externalSystemId}</td>
    <td>{site.name || '—'}</td>
    <td>{site.address || '—'}</td>
    <td className="text-capitalize">{site.status}</td>
    <td className="text-capitalize">{site.evidence?.result?.replace(/_/g, ' ') || '—'}</td>
    <td>{formatProduction(site.productionReadings)}</td>
    <td>{formatTimestamp(site.evidence?.lastObservedAt?.toString())}</td>
    <td>{formatTimestamp(site.lastSyncedAt)}</td>
    <td>{site.lastSyncErrorCategory || '—'}</td>
    <td>
      <Link to={`/owner-monitoring/${site.id}`}>View details</Link>
    </td>
  </tr>
)

export const OwnerMonitoringGrantsPage = () => {
  const { fetching, error, data } = useApi<MonitoringSiteSummary[]>(
    '/solar-monitoring/owner/sites',
  )
  const sites = data || []

  return (
    <Section>
      <h4>Owner monitoring sites</h4>
      <p>
        Sites discovered through authorizations submitted by system owners.
      </p>
      {fetching ? <Spinner size={SpinnerSize.large} /> : null}
      {error ? (
        <div className="alert alert-danger">
          {typeof error === 'string' ? error : error.message}
        </div>
      ) : null}
      {!fetching && !error && sites.length === 0 ? (
        <div className="alert alert-secondary">No monitoring sites yet.</div>
      ) : null}
      {!fetching && sites.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Site ID</th>
                <th>Name</th>
                <th>Address</th>
                <th>Status</th>
                <th>Production status</th>
                <th>Recent production</th>
                <th>Latest observation</th>
                <th>Last sync</th>
                <th>Last error</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sites.map(site => (
                <SiteRow key={site.id} site={site} />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Section>
  )
}
